'use client';

import {
  Select as NextUISelect,
  SelectProps,
  SelectItem,
} from '@heroui/select';
import { SlotsToClasses, SelectSlots } from '@heroui/theme';
import { SharedSelection } from '@heroui/system-rsc';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';
// import Icon from "../Common/Icon";
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface CustomSelectProps
  extends Omit<SelectProps, 'children' | 'classNames' | 'variant'> {
  data?: {
    value: string | number;
    label: string | number;
  }[];
  children?: JSX.Element[] | JSX.Element;
  isTranslatedData?: boolean;
  isClearable?: boolean;
  classNames?: SlotsToClasses<SelectSlots> & { container?: string };
  variant?: 'outline';
}

const Select = ({
  data,
  children,
  isTranslatedData = false,
  isClearable: isClearableProp,
  variant,
  ...props
}: CustomSelectProps) => {
  const t = useTranslations();

  const [value, setValue] = React.useState(
    new Set(props.selectedKeys) || new Set([])
  );

  const isClearable = useMemo(
    () => isClearableProp && data?.some((item) => value.has(item.value)),
    [isClearableProp, value, data]
  );

  const handleClear = () => {
    props.onChange?.({
      target: {
        name: props.name,
        value: '',
      },
    } as React.ChangeEvent<HTMLSelectElement>);
    setValue(new Set([]));
  };

  const onHandleSelectionChange = (data: SharedSelection) => {
    setValue(new Set(data));
    props.onSelectionChange?.(data);
  };

  return (
    <div className={clsx('relative', props.classNames?.container)}>
      {isClearable && (
        <div
          className="absolute z-50 right-3 p-0 top-1/2 -translate-y-1/2 cursor-pointer w-[22px] h-[22px] hover:bg-black/10 rounded-full transition-background flex items-center justify-center"
          onClick={handleClear}
        >
          <FontAwesomeIcon icon="xmark" />
        </div>
      )}
      <NextUISelect
        {...props}
        placeholder={
          isTranslatedData
            ? props.placeholder
              ? t(props.placeholder)
              : props.placeholder
            : props.placeholder
        }
        {...(props.selectorIcon && {
          selectorIcon: <div>{props.selectorIcon}</div>,
        })}
        {...(isClearable && {
          selectorIcon: <div></div>,
        })}
        onSelectionChange={onHandleSelectionChange}
        selectedKeys={value}
        classNames={{
          ...(props.selectorIcon && {
            selectorIcon: 'static w-auto h-auto',
          }),
          ...(variant === 'outline' && {
            trigger: clsx(
              'border border-gray-light2 h-12 bg-transparent data-[hover=true]:bg-transparent group-hover:opacity-80 shadow-none transition-opacity'
            ),
            value: clsx('body2 text-gray-lighter'),
          }),
        }}
      >
        {children ||
          (data?.map((item) => (
            <SelectItem key={item.value}>
              {isTranslatedData ? t(item.label + '') : item.label}
            </SelectItem>
          )) as JSX.Element[])}
      </NextUISelect>
    </div>
  );
};

Select.displayName = 'Select';
export default Select;
